import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Button, EmptyState, Modal, Spinner, TextInput } from '@opendate-app/setlist';
import { toastSuccess, toastError } from "../../../shared/toastHelper";

const errorMessage = (err, fallback) => err?.response?.data?.errors?.join(', ') || fallback;

const SectionPricesModal = ({ open, onClose, seatingChart, csrfToken }) => {
  const [sections, setSections] = useState(null);
  const [prices, setPrices] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    setSections(null);
    axios.get(`/seating_charts/${seatingChart.id}/section_prices`)
      .then(({ data }) => {
        if (ignore) return;
        setPrices(Object.fromEntries(data.sections.map(section => [section.key, { price: section.price || '', day_of_show_price: section.day_of_show_price || '' }])));
        setSections(data.sections);
      })
      .catch((err) => {
        if (ignore) return;
        toastError(errorMessage(err, 'There was an error loading the section prices.'));
        onClose();
      });
    return () => { ignore = true; };
  }, [open, seatingChart.id]);

  const save = () => {
    setSaving(true);
    axios.patch(`/seating_charts/${seatingChart.id}/section_prices`, { section_prices: prices }, { headers: { 'X-CSRF-TOKEN': csrfToken } })
      .then(() => {
        toastSuccess('Section prices saved.');
        onClose();
      })
      .catch((err) => toastError(errorMessage(err, 'There was an error saving the section prices.')))
      .finally(() => setSaving(false));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Section prices: ${seatingChart.name}`}
      description="New shows that use this seat map start each section at this price. A price on the show's offer with the same name as the section comes first and skips that section's day-of-show price; a blank price starts at $20. A day-of-show price takes over when doors open (or at show start if there are no doors); leave it blank for no change. Shows already set up keep their prices."
      footer={<>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" onClick={save} disabled={!sections?.length || saving}>Save</Button>
      </>}
    >
      {sections === null && <Spinner />}
      {sections?.length === 0 && <EmptyState title="This seat map has no sections yet" description="Add sections in the seat map designer, then set their prices here." />}
      {sections?.map(section => (
        <div key={section.key} className="mb-3">
          <p className="mb-1" style={{ fontWeight: 600 }}>{section.label}</p>
          <div className="d-flex flex-wrap" style={{ gap: 12 }}>
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <TextInput
                label="Price"
                aria-label={`${section.label} price`}
                leadingText="$"
                placeholder="20.00"
                inputMode="decimal"
                value={prices[section.key].price}
                onChange={(e) => setPrices({ ...prices, [section.key]: { ...prices[section.key], price: e.target.value } })}
              />
            </div>
            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
              <TextInput
                label="Day of show"
                aria-label={`${section.label} day of show price`}
                leadingText="$"
                placeholder="No change"
                inputMode="decimal"
                value={prices[section.key].day_of_show_price}
                onChange={(e) => setPrices({ ...prices, [section.key]: { ...prices[section.key], day_of_show_price: e.target.value } })}
              />
            </div>
          </div>
        </div>
      ))}
    </Modal>
  );
};

SectionPricesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  seatingChart: PropTypes.object.isRequired,
  csrfToken: PropTypes.string.isRequired,
};

export default SectionPricesModal;
